import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Scale, Landmark, Shield, Users, GraduationCap, Briefcase, AlertTriangle, UserPlus, Heart, Hand, BookOpen, MapPin, Play, Volume2, VolumeX } from 'lucide-react';
import { siteConfig, issues, testimonials, actions } from '../data/mock';
import Newsletter from '../components/Newsletter';
import { MesoamericanBackground, PyramidDivider, CalendarMotif, SerpentBorder } from '../components/MesoamericanPatterns';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  Scale: Scale,
  Landmark: Landmark,
  Shield: Shield,
  Users: Users,
  GraduationCap: GraduationCap,
  Briefcase: Briefcase,
  AlertTriangle: AlertTriangle,
  UserPlus: UserPlus,
  Heart: Heart,
  Hand: Hand,
  BookOpen: BookOpen
};

const Home = () => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  // Unmute video on ANY page interaction
  useEffect(() => {
    const unmute = () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        setMuted(false);
      }
      // Remove listeners after first interaction
      ['click', 'touchstart', 'scroll', 'keydown'].forEach(e => 
        document.removeEventListener(e, unmute)
      );
    };

    ['click', 'touchstart', 'scroll', 'keydown'].forEach(e => 
      document.addEventListener(e, unmute, { passive: true })
    );

    return () => {
      ['click', 'touchstart', 'scroll', 'keydown'].forEach(e => 
        document.removeEventListener(e, unmute)
      );
    };
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !muted;
      videoRef.current.muted = newMuted;
      setMuted(newMuted);
    }
  };
  
  return (
    <div className="bg-amber-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* Light warm background */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100" />
        <MesoamericanBackground opacity={0.06} />
        
        {/* Decorative calendar motifs */}
        <div className="absolute top-32 left-10 opacity-30 hidden lg:block">
          <CalendarMotif size={120} />
        </div>
        <div className="absolute bottom-32 right-10 opacity-30 hidden lg:block">
          <CalendarMotif size={100} />
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo Image */}
            <div className="flex justify-center mb-8">
              <img 
                src={siteConfig.logo} 
                alt="Xicano Rights Union"
                className="w-48 md:w-64 lg:w-80 h-auto drop-shadow-lg"
              />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-stone-800 leading-tight mb-6">
              {t('home.heroTitle')}
            </h1>
            
            <p className="text-xl md:text-2xl text-stone-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/report-violation"
                className="px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-500/25 flex items-center justify-center gap-2"
              >
                {t('home.reportViolation')}
                <AlertTriangle size={20} />
              </Link>
              <Link
                to="/join"
                className="px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/25 flex items-center justify-center gap-2"
              >
                {t('home.joinMovement')}
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <div className="w-6 h-10 border-2 border-teal-600/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-teal-600 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Mission Statement Banner with Serpent Border */}
      <section className="relative">
        <SerpentBorder className="bg-teal-600" />
        <div className="py-12 bg-gradient-to-r from-teal-600 via-teal-500 to-teal-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xl md:text-2xl text-white text-center font-medium leading-relaxed">
              {t('home.missionQuote')}
            </p>
          </div>
        </div>
        <SerpentBorder className="bg-teal-600 rotate-180" />
      </section>

      {/* Breaking News / Top Stories */}
      <section className="py-16 relative bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <h2 className="text-2xl font-bold text-white">{t('home.topStories')}</h2>
            </div>
            <Link
              to="/news"
              className="text-teal-400 hover:text-teal-300 font-medium flex items-center gap-1"
            >
              {t('home.viewAllNews')}
              <ArrowRight size={16} />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Alex Pretti Memorial */}
            <Link
              to="/news#memorial-alex"
              className="group p-6 bg-gradient-to-br from-stone-700 to-stone-800 border border-stone-600 hover:border-red-500/50 rounded-xl transition-all duration-300"
            >
              <div className="flex items-center gap-2 mb-3">
                <Heart className="text-red-400" size={18} />
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded">Memorial</span>
                <span className="text-stone-500 text-sm">January 24, 2026</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
                In Memory of Alex Pretti (1989-2026)
              </h3>
              <p className="text-stone-300 text-sm leading-relaxed mb-4">
                An ICU nurse and hero who was shot by Border Patrol agents while documenting federal immigration enforcement. His final act was to protect others. Rest in Power.
              </p>
              <span className="text-teal-400 text-sm font-medium flex items-center gap-1">
                {t('common.readMore')}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            
            {/* Renee Good Memorial */}
            <Link
              to="/news#memorial-renee"
              className="group p-6 bg-gradient-to-br from-stone-700 to-stone-800 border border-stone-600 hover:border-red-500/50 rounded-xl transition-all duration-300"
            >
              <div className="flex items-center gap-2 mb-3">
                <Heart className="text-red-400" size={18} />
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded">Memorial</span>
                <span className="text-stone-500 text-sm">January 7, 2026</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
                In Memory of Renee Nicole Good (1988-2026)
              </h3>
              <p className="text-stone-300 text-sm leading-relaxed mb-4">
                A 37-year-old U.S. citizen fatally shot by an ICE agent in Minneapolis. Her death has sparked protests and renewed calls for accountability in immigration enforcement.
              </p>
              <span className="text-teal-400 text-sm font-medium flex items-center gap-1">
                {t('common.readMore')}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            
            {/* Minnesota AG Legal Action */}
            <Link
              to="/news"
              className="group p-6 bg-gradient-to-br from-stone-700 to-stone-800 border border-stone-600 hover:border-teal-500/50 rounded-xl transition-all duration-300"
            >
              <div className="flex items-center gap-2 mb-3">
                <Scale className="text-teal-400" size={18} />
                <span className="px-2 py-1 bg-teal-500/20 text-teal-400 text-xs font-medium rounded">Legal Action</span>
                <span className="text-stone-500 text-sm">January 2026</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-teal-400 transition-colors">
                Minnesota AG Fights to End Operation Metro Surge
              </h3>
              <p className="text-stone-300 text-sm leading-relaxed mb-4">
                AG Keith Ellison takes unprecedented legal action after two U.S. citizens killed by federal agents. State sues for access to evidence.
              </p>
              <span className="text-teal-400 text-sm font-medium flex items-center gap-1">
                {t('common.readMore')}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
            
            {/* Three Shootings Story */}
            <Link
              to="/news"
              className="group p-6 bg-gradient-to-br from-stone-700 to-stone-800 border border-stone-600 hover:border-amber-500/50 rounded-xl transition-all duration-300"
            >
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="text-amber-400" size={18} />
                <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded">Alert</span>
                <span className="text-stone-500 text-sm">January 2026</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                Three Federal Agent Shootings in Three Weeks
              </h3>
              <p className="text-stone-300 text-sm leading-relaxed mb-4">
                Minneapolis Police Chief calls attention to unprecedented violence. Governor Walz condemns "campaign of organized brutality."
              </p>
              <span className="text-teal-400 text-sm font-medium flex items-center gap-1">
                {t('common.readMore')}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
          
          {/* Alert Map Banner */}
          <div className="mt-8 p-6 bg-gradient-to-r from-red-600 to-red-700 rounded-xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <MapPin className="text-white" size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('home.alertMapBanner.title')}</h3>
                  <p className="text-white/80">{t('home.alertMapBanner.subtitle')}</p>
                </div>
              </div>
              <Link
                to="/alert-map"
                className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <span>{t('home.alertMapBanner.viewMap')}</span>
                <span className="w-2 h-2 bg-stone-900 rounded-full animate-pulse" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Issues We Fight For */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-orange-50 to-amber-100" />
        <MesoamericanBackground opacity={0.05} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <PyramidDivider className="mb-8" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-800 mb-4">
              {t('home.whatWeDo')}
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              {t('home.whatWeDoSubtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {issues.map((issue, index) => {
              const IconComponent = iconMap[issue.icon];
              const colors = [
                { bg: 'bg-teal-100', iconBg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200 hover:border-teal-400' },
                { bg: 'bg-amber-100', iconBg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200 hover:border-amber-400' },
                { bg: 'bg-red-100', iconBg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200 hover:border-red-400' },
              ];
              const colorScheme = colors[index % 3];
              
              return (
                <div
                  key={issue.id}
                  className={`group p-6 bg-white/80 backdrop-blur-sm border-2 ${colorScheme.border} rounded-xl transition-all duration-500 hover:shadow-lg hover:-translate-y-1`}
                >
                  <div className={`w-14 h-14 ${colorScheme.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-stone-800 mb-3">{issue.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">
                    {issue.description}
                  </p>
                  <p className={`${colorScheme.text} text-sm font-semibold`}>{issue.stat}</p>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/programs"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              {t('common.learnMore')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Take Action Section */}
      <section className="py-24 relative bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Take <span className="text-amber-400">Action</span>
            </h2>
            <p className="text-lg text-stone-300">
              {t('home.testimonials.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {actions.map((action, index) => {
              const IconComponent = iconMap[action.icon];
              return (
                <Link
                  key={index}
                  to={action.link}
                  className="group p-6 bg-stone-700/50 border border-stone-600 hover:border-teal-500 rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="text-white" size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-stone-300 text-sm">
                    {action.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* CTA Section */}
      <section className="py-24 relative bg-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('home.joinFight')}
          </h2>
          <p className="text-lg text-teal-100 mb-10 max-w-2xl mx-auto">
            {t('home.joinFightSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/join"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold text-lg rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/25"
            >
              {t('home.becomeMember')}
              <ArrowRight size={22} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white/10 border-2 border-white text-white hover:bg-white hover:text-teal-700 font-semibold text-lg rounded-lg transition-all duration-300"
            >
              {t('home.donateNow')}
              <Heart size={22} />
            </Link>
          </div>
        </div>
      </section>

      {/* Community Video Section */}
      <section className="py-16 relative bg-stone-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-4">
              <Play className="text-amber-400" size={18} />
              <span className="text-amber-400 font-medium text-sm">{t('home.communityVideo.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t('home.communityVideo.title')}
            </h2>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <video
              ref={videoRef}
              className="w-full aspect-video"
              controls
              autoPlay
              muted={muted}
              loop
              playsInline
              poster={siteConfig.heroImage}
            >
              <source src={siteConfig.communityVideo.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Large Sound Toggle Button */}
            <button
              onClick={toggleMute}
              className="absolute top-4 right-4 z-10 px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-full flex items-center gap-2 shadow-lg transition-all animate-pulse"
            >
              {muted ? (
                <>
                  <VolumeX size={24} />
                  <span>TAP FOR SOUND</span>
                </>
              ) : (
                <>
                  <Volume2 size={24} />
                  <span>SOUND ON</span>
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
